import { Button } from '@client/components/ui/button';
import { Plus, ExternalLink, Loader2 } from 'lucide-react';
import { formatCurrency } from '@shared/formatCurrency';
import { useSubscriptionActions } from './useSubscriptionActions';
import { CancellationModal } from '@client/features/stripe-subscription-cancellation/components/cancellation-modal';
import { SubscriptionState } from '@client/model';
import { useSelector } from 'react-redux';
import { selectSession } from '@client/model/selectors';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@design-system';

function MetricCell({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="mb-1 text-xs text-gray-700">{label}</p>
      <p className="text-sm font-medium text-gray-900">{value}</p>
    </div>
  );
}

interface SubscriptionOverviewCardProps {
  subscription: SubscriptionState;
  activeSeatsCount: number;
  creditBalance: number;
  unitPricePerSeat: number;
  isOwner: boolean;
  onAddSeat: () => void;
  hideManagementActions?: boolean;
  subscriptionSeats?: SubscriptionSeat[];
  refetchSubscriptionSeats?: () => void;
}

export function SubscriptionOverviewCard({
  subscription,
  activeSeatsCount,
  creditBalance,
  unitPricePerSeat,
  isOwner,
  onAddSeat,
  hideManagementActions = false,
  subscriptionSeats = [],
  refetchSubscriptionSeats,
}: SubscriptionOverviewCardProps) {
  const { advisor } = useSelector(selectSession);
  const {
    handleManageBilling,
    isLoadingBillingPortal,
    isCancellationModalOpen,
    handleOpenCancellationModal,
    handleCloseCancellationModal,
  } = useSubscriptionActions({ subscription });
  const { flags } = useFeatureFlags();

  const totalSeats = subscription?.totalSeats || 0;
  const billingInterval = subscription?.billingInterval || 'month'; // Default to 'month' client-side
  const isAnnual = billingInterval === 'year';
  const periodCharge = totalSeats * unitPricePerSeat;
  const seatMonths = unitPricePerSeat > 0 ? creditBalance / unitPricePerSeat : 0;

  // Use Stripe API data as source of truth, fallback to calculated values
  const invoiceAmount = subscription?.nextInvoiceAmount ?? periodCharge;
  const invoiceDate = subscription?.nextInvoiceDate ?? subscription?.nextBillingDateTime;

  // Check if any seat has cancellation metadata
  const ownerSeat = subscriptionSeats.find(seat => seat.advisorId === advisor?.advisorId);
  const hasCancellationData = ownerSeat?.metadata?.cancellation_timestamp;
  const cancellationDate = hasCancellationData ? subscription?.nextBillingDateTime : null;

  // Format date for display
  const formatDate = (date: Date | null | undefined): string => {
    if (!date) return '';
    return new Date(date).toLocaleDateString();
  };

  // Show trial end date only if status is 'trialing' and trialEndDateTime is not null
  const showTrialEndDate =
    subscription?.subscriptionStatus === 'trialing' && subscription?.trialEndDateTime;

  // Check if subscription is canceled or unpaid
  const isSubscriptionInactive =
    subscription?.subscriptionStatus === 'canceled' ||
    subscription?.subscriptionStatus === 'unpaid';

  const subscriptionStatus = subscription?.subscriptionStatus ?? 'unknown';

  // Format status for display
  const formatStatus = (status: string): string => {
    return status.includes('_') ? status.split('_').join(' ') : status;
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    if (status === 'active') return 'text-green-700';
    if (status === 'trialing') return 'text-violet-700';
    if (status === 'canceled' || status === 'unpaid') return 'text-red-700';
    return 'text-gray-700';
  };

  return (
    <>
      <div className="mb-8 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5">
          <div
            className={`flex flex-wrap items-end gap-8 ${isSubscriptionInactive ? 'opacity-50' : ''}`}
          >
            <div>
              <p className="mb-1 text-xs text-gray-700">Status</p>
              <p className={`text-sm font-medium capitalize ${getStatusColor(subscriptionStatus)}`}>
                {formatStatus(subscriptionStatus)}
                {showTrialEndDate && (
                  <span className="ml-2 text-xs text-gray-700">
                    (ends: {formatDate(subscription.trialEndDateTime)})
                  </span>
                )}
              </p>
            </div>
            <MetricCell
              label="Total Seats"
              value={totalSeats}
            />
            <MetricCell
              label="Seat Cost"
              value={`${formatCurrency(unitPricePerSeat, 2)}${isAnnual ? '/year' : '/month'}`}
            />
            <div>
              <p className="mb-1 text-xs text-gray-700">Total</p>
              <p className="text-sm font-medium text-gray-900">
                {formatCurrency(invoiceAmount, 2)}
                {invoiceDate && (
                  <span className="ml-2 text-xs text-gray-700">
                    (next invoice: {formatDate(invoiceDate)})
                  </span>
                )}
              </p>
            </div>
            {creditBalance > 0 && (
              <MetricCell
                label="Prepaid Credits/Balance"
                value={`${formatCurrency(creditBalance, 2)}${seatMonths > 1 ? ` (${seatMonths.toFixed(1)} seat-months)` : ''}`}
              />
            )}
          </div>

          {isOwner && !hideManagementActions && !isSubscriptionInactive && (
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <Button
                onClick={onAddSeat}
                className="flex items-center gap-2"
                disabled={
                  subscription?.subscriptionStatus !== 'active' &&
                  subscription?.subscriptionStatus !== 'trialing'
                }
              >
                <Plus className="h-4 w-4" />
                Add Seat
              </Button>
              <Button
                variant="outline"
                onClick={handleManageBilling}
                disabled={isLoadingBillingPortal}
                className="flex min-w-[200px] items-center gap-2"
              >
                {isLoadingBillingPortal ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Loading Portal...</span>
                  </>
                ) : (
                  <>
                    <ExternalLink className="h-4 w-4" />
                    <span>Manage Billing in Stripe</span>
                  </>
                )}
              </Button>
              {flags.stripeSubscriptionCancellation && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleOpenCancellationModal}
                          disabled={hasCancellationData}
                          className="text-gray-700"
                        >
                          Cancel Subscription
                        </Button>
                      </span>
                    </TooltipTrigger>
                    {hasCancellationData && cancellationDate && (
                      <TooltipContent>
                        <p>
                          Your subscription is already cancelled and will end on{' '}
                          {formatDate(cancellationDate)}
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
        </div>
      </div>

      <CancellationModal
        isOpen={isCancellationModalOpen}
        onClose={handleCloseCancellationModal}
        refetchSubscriptionSeats={refetchSubscriptionSeats}
      />
    </>
  );
}
